<template>
<div class="chapter">
<div class="inner_page">
<a id="page_1"></a>
<h2 class="ct">TUSEDAY, AUGUST 9, 1988</h2>
<h2 class="ct1">THE FIRST DAY</h2>
<a id="page_2"></a>
<a id="page_3"></a>
<p class="noindent"><span class="bold">7:55 A.M.</span> The ground floor hallway of the
            Capitol was choked with yellers and shovers. The staffers yelled, the Secret Service
            shoved, and Sally Crain was stuck in the middle, wondering why all the power and the
            glory of the United States government couldn’t keep the welcome reception for
            Colonel Octavio Martinez from turning into a riot. She was one of those earnest, honest
            women who began to appear in Washington under Carter. Her fair hair was her own, and her
            eyes were blue and wide-set. She had healthy, ruddy skin, like a freckled kid who has
            grown up slow and easy. And she had a long, loose gait, the way tall women do when
            they’re confident in their sex.</p>
<p class="indent">Sally did her best to push her way upstream, smiling and nodding her
            apologies, trying to work her way to Martinez. But the crush and the summer heat were
            stifling. At last, she stepped into a doorway, leaned out of the way, and waited while
            the noisy mob oozed down the hall; bringing Martinez to her.</p>
<p class="indent">She could just make him out at the far end of the corridor, dark and
            handsome in his beret and fatigues, trying to shake every eager hand, flashing fine
            white teeth under his, thick mustache. She had followed his campaigns in the Nicaraguan
            jungle with a mixture of professional detachment and sentimental pride. He was the stuff
            of legend—a high school algebra teacher who had watched his sweaty little country
            evolve from an oligarchy of the rich to a tyranny of the poor to a dictatorship of <em class="italic">Comunistas</em>. All that while, he had gone on patiently
            unraveling the mysteries of sine and cosine to classes of disinterested adolescents.
            Year by year, the textbooks grew more tattered and the rows of empty seats crept forward
            toward his desk. Until one day he realized that the seats were empty because boys who
            should have been in high school were in the hills of Jinotega Province, carrying
            M-16’s and bleeding to death in the mud. So, he kissed his wife and sons, put
            some socks in his old briefcase and rode a wheezing yellow bus to the end of the line,
            and walked into the jungle to find his students.</p>
<a id="page_4"></a>
<p class="indent">The first man he killed, he killed with a machete, and the blood spurted
            out of the man’s neck into Martinez’s face and made him vomit. After that,
            they gave him a pistol, and when the next Contra died, Martinez inherited his M-16. That
            winter, he led the raid that took the town of Ocotal and held it three days against a
            government battalion. The grateful Contras made him a major in their ragtag army. And in
            the end, the peasants adored him, the CIA pushed him, and he became <em class="italic">Colonel Reynaldo</em>—the leader, the inspiration, the fox.</p>
<p class="indent">Now, President Baker and his administration pointed to Octavio Martinez
            and his war as the true popular revolution that would bring representative democracy to
            that turbulent isthmus in the middle of our hemisphere. And the one-time algebra
            instructor, who now taught teenage boys how to plant Claymores and ambush recon patrols,
            had been flown to Washington to do the rounds of the committees with his tales of
            barefoot freedom fighters in the jungles, praying to the Blessed Virgin that the <em class="italic">Yanquis</em> would heed their cry for aid. Heroic Colonel Martinez
            would be the perfect counterpoint to the bitter memories of Colonel North, Irangate, and
            the illegal diversion of funds.</p>
<p class="indent">The crowd swirled by and Sally saw her chance. She held her clipboard to
            her bosom, stepped quickly between the Secret Service men, so close to Martinez that she
            almost bumped him, shouting to be heard above the din.</p>
<p class="indent">“Colonel, I’m Sally Crain. I’m Senator
            Fallon’s press aide.”</p>
<p class="indent">Martinez shot her that quick, dazzling smile, but waved his hand beside
            his ear to show her that he didn’t understand.</p>
<p class="indent">Sally fought to keep her place beside him as the noisy mob slid down the
            hallway toward daylight and the exit to the Capitol steps. She switched to Spanish.</p>
<p class="indent"><em class="italic">“Por favor. Me llamo Sally Crain, Soy assistante
                periodista del—”</em></p>
<p class="indent">In the din, Martinez shrugged, baffled.</p>
<p class="indent">Sally caught her breath and shouted in Portuguese, “<em class="italic">O meu nome é Sally Crain. Sou journalista
                assistante</em>—”</p>
<p class="indent">Martinez only smiled and shook his head. And as they reached the doorway,
            he left her behind, trying hard to look like a lady and shouting <em class="italic">“Je suis Sally Crain, une assistante-presse</em> …”</p>
<p class="indent">But he was gone, through the door into the daylight, far out of
            earshot.</p>
<p class="indent">Sally surrendered to the swell of the crowd rising behind her, let the
            pushing, jabbering mob carry her out. She stood blinking, blinded by the harsh daylight,
            letting the sea of humanity sweep <a id="page_5"></a>past her on both sides. And she
            said, half aloud, half to herself, “Dammit—what language does he
            speak?”</p>
<p class="indent">Suddenly, as if from nowhere, Martinez was swept past her. Gently, he
            caught her arm and drew her against him so that the crowd bore them along the promenade
            like rising surf. She looked up, astonished, as the thronging well-wishers pressed them
            together. She could feel the rough canvas of his fatigues against the backs of her arms.
            She could sense his body moving under the cloth. His eyes were dark and glinting, and
            his voice deep under the chatter of the crowd. It was as though, for an instant, they
            were intimately alone.</p>
<p class="indent">“I speak all of them,” he said. “Your Spanish is
            excellent. But your Portuguese …” He shook his head and clucked his
            tongue.</p>
<p class="indent">Sally stopped where she was, speechless. And as she stood gaping, the
            surging crowd bore Martinez away down the steps toward the rostrum and the impatient
            television news crews.</p>
<p class="indent">She stood, feeling punctured and ridiculous. Then Martinez looked back up
            at her and winked. That made her laugh out loud.</p>
<p class="indent">“Shit,” she said, and trailed after him down the steps,
            feeling girlish and silly and charmed.</p>
<p class="indentt">Martinez was shaking hands down a long line of well-fed, dark-suited
            dignitaries by the time Sally climbed the stairs to the wooden rostrum. She checked her
            watch: 8:09. She knew that in NBC Studio 3B in New York, the <em class="italic">Today</em> show’s Willard Scott was just wrapping up the weather report.
            In Control Room 3B, Steve Chandler, the producer, was on the intercom to Bryant Gumbel,
            reminding him to tell the audience that, after the next commercial, <em class="italic">Today</em> would switch live to Washington.</p>
<p class="indent">Sally knew it because she had guaranteed Chandler that Senator Terry
            Fallon would get up and welcome Martinez at 8:11 on the button. She’d also
            promised him an exclusive Gumbel interview with Terry and Martinez on
            Wednesday—provided Chandler would agree to cover the welcome live. Chandler took
            the deal, even though he knew he was being used.</p>
<p class="indent">He knew—and so did Sally—that if one network morning news
            took the event live, the other two had to follow suit or let <em class="italic">Today</em> have an exclusive. The welcome on Tuesday and the interview on
            Wednesday assured Terry of national exposure on two successive days. And the morning
            ratings were small enough that, if it were a slow news day, the welcome could be
            replayed on the noon and evening news.</p>
<a id="page_6"></a>
<p class="indent">Chandler knew all that—and he knew that Sally knew it, too. He knew
            she was using the competition among the three networks to advance Terry Fallon’s
            career. But Chandler went along because he also knew that Sally would get the Secret
            Service to set the rostrum at the Senate end of the Capitol so that the morning sun
            would be behind the speakers, haloing their hair instead of glaring in their eyes. He
            knew that Sally would get Fallon out of his chair at exactly 8:11 come hell or high
            water. He knew that whatever Fallon had to say would run twenty-five seconds or less,
            the ideal length for an audio bite in a news feature. And he knew that Sally would coach
            Fallon to play it to the camera with the peacock. In fact, Chandler was so confident
            that Sally would manage it down to the second, he was not at all surprised to see her
            standing at the podium behind the Great Seal of the United States of America when <em class="italic">Today</em> went to commercial and the Washington feed came up on
            the line.</p>
<p class="indentt">When the red tally light on the NBC camera blinked on, Sally stood at the
            podium, looking down at the busy news crews and gossiping print reporters ringing the
            rostrum. Twenty microphones stared her in the face. She checked her watch: 8:10. She
            began. “Senator Fallon will make a brief—”</p>
<p class="indent">Then she realized that her mikes were dead and she was talking to herself.
            “Audio, open number 4, please,” she said.</p>
<p class="indent">Suddenly, the whistle of electronic feedback cut through the air.
            Instinctively, people ducked and held their ears.</p>
<p class="indent">Sally’s voice boomed <em class="italic">“Thank
            you</em>…. Then the sound man cut the gain to normal levels. Sally smiled as
            the crowd settled down. The gambit always worked.</p>
<p class="indent">“Thank you,” she said, and her voice echoed between the
            Capitol and the Russell Building across the street. “Senator Fallon will make a
            brief welcoming statement. White sheet with blue border.”</p>
<p class="indent">She held it up and gave the reporters a moment to find their copies.</p>
<p class="indent">“Then Colonel Martinez will respond with this
            text—”</p>
<p class="indent">She looked at the sheet—then over her shoulder at Martinez. There
            was a puckish twinkle in his eye and, despite herself, she smiled back.</p>
<p class="indent">“—in <em class="italic">English,”</em> she said.
            “Red border.” She held it up. “Translations are in yellow.”
            She held up one of those. “Foreign press, if you need one, see Betty.”
            Sally waved, and Betty, <a id="page_7"></a>her secretary, waved back from behind the
            cameras. Four or five foreign reporters hustled for a copy of the translation.</p>
<p class="indent">Sally let her eyes sweep down across the news crews. They were all there:
            NBC, CBS, ABC, the BBC, the CBC, TF1, every country in Latin America, a dozen Iron
            Curtain countries. What Terry would say would be carried to every city, every hamlet in
            America, virtually every country on earth. She had organized it, she had arranged it,
            she had planned it down to the last detail. The morning light glistened in her eyes. The
            reporters and cameramen stared silently up at her. The only sound was the soft breeze
            off the Potomac and the snap of the flag before the Capitol dome. Sally raised her face
            into the wind, and her eyes were slits of satisfaction.</p>
<p class="indent">“Roll tape,” she said.</p>
<p class="indent">In the television mobile units, directors parroted her command.</p>
<p class="indent">On the NBC television network, Bryant Gumbel was looking into the camera
            and saying “—to the steps of the U.S. Capitol where Senator Terry Fallon
            will welcome the Nicaraguan freedom fighter who—”</p>
<p class="indent">Sally smiled. “Ladies and gentlemen, the senator from Texas, the
            Honorable Terrence Fallon.” As the applause started, she made her way to the side
            of the rostrum, then down the stairs to where she could stand looking up at Terry.</p>
<p class="indent">He crossed the boards with a loping, easy stride. There was so much of the
            boy in him, so much of the athlete, that she had to remind herself he was a senator now,
            one of the hundred who guided the destiny of the nation. When Julio Ramirez, the Contra
            spokesman-in-exile, had requested Terry make the speech welcoming Martinez, Sally had
            jumped at the chance. And the White House had two reasons to hasten to agree. First,
            Senator Fallon had leadership on Central America. He wouldn’t roll over for
            Castro, Ortega, or the Sandinistas. He wanted to fight the good fight the same way the
            White House, and the voters did: our money, our guns, our advisors—their blood.
            The other reason was even more apparent: the dimpled, open-handed, kid-from-Texas grin
            that had been Terry’s hallmark since he entered the House of Representatives from
            Houston in 1978.</p>
<p class="indent">Some people wondered if he could ride his good looks and
            moderate-conservative ideas all the way to the Senate. But he didn’t have to.
            When the ABSCAM net caught Senator Caleb Weatherby in 1984, the governor of Texas
            appointed Terry to fill the unexpired term. Overnight, Representative Fallon became <a id="page_8"></a>Senator Fallon. And Washington took note; he was a young man on his
            way.</p>
<p class="indent">Bryant Gumbel said, “—live from Washington, Senator Terry
            Fallon.” In Control Room 3B, Steve Chandler said, “One on the
            line,” and, on cue, the NBC network fed America a picture of Terry at the podium.
            In the dark of the control room, a technician whistled through his teeth, and someone
            else said, “Amazing …”</p>
<p class="indent">Chandler laughed out loud. “Supercunt,” he said, and the
            technicians knew that in his mouth it was the ultimate professional compliment.</p>
<p class="indent">The applause for Terry was warm, sustained. In fact, it began to
            build—until Terry raised his hands for silence. “My fellow
            Americans,” he began, in a loud declamatory voice. The crowd settled down. Then
            his tone changed—as Sally had taught him—to something approaching
            tenderness, and a warm light came on in his eyes. “My fellow Americans … For more than two hundred years, the brave men and women of this hemisphere have carried the
            flag of freedom against the forces of oppression. Since 1981, Colonel Octavio Martinez
            has led the daring and historic struggle to free his Nicaraguan homeland from Communist
            dictators.”</p>
<p class="indent">Sally looked up at Terry as he spoke, watched the soft breeze lift the
            ends of reddish hair from his temples. She watched the way his eyes moved from face to
            face among the crowd, how they greeted people, spoke to them alone, one by one, how his
            smile was ready, and how you knew by the way he held his shoulders that the world would
            be all right. She glanced over at Martinez.</p>
<p class="indent">But the colonel wasn’t watching Terry or listening to his remarks.
            He was staring straight at Sally. Then he nodded toward Terry—nodded to tell her
            he understood how she felt about the thin young man at the podium. There was something
            sweet about this hero, something powerfully seductive. Shyly, Sally lowered her
            eyes.</p>
<p class="indent">Fallon said, “I am proud to welcome you, Colonel, in the name of
            the people of the United States. And in the name of all people everywhere who love
            freedom and peace.”</p>
<p class="indent">Steve Chandler looked up at the clock on the control room wall.
            Twenty-five seconds on the nose.</p>
<p class="indent">Martinez rose and walked to the podium. The dignitaries, the onlookers,
            even the reporters broke into hearty applause. Terry <a id="page_9"></a>clasped
            Martinez’s hand and then his arm. The applause rose higher. The dignitaries on
            the rostrum stood, pounding their hands together. Then the handshake became a warm
            embrace as the two dashing young men threw their arms about each other. The crowd below
            the rostrum came to its feet. And the roar swelled so loud that the gunshots would have
            gone unnoticed, would have sounded like Chinese firecrackers in another street, if it
            had not been for the way the bullets seemed to explode as they struck.</p>
<p class="indent">There was a moment just after the last shot was fired, as the applause was
            choking into silence, when the two men clung together like drunken dancers propped on
            one another’s shoulder. And if it had not been for the blood that stained their
            jackets and trousers and streamed down their pants legs; had it not been for the bare
            bones showing in Martinez’s back where the rounds had torn away his flesh; had it
            not been for the blood and tissue that spattered on the clothes and collars and faces of
            the horrified dignitaries behind them—had it not been for those grotesqueries the
            crowd would have thought they had witnessed a burlesque turn, not a political
            assassination.</p>
<p class="indent">Steve Chandler said, “What the fuck…?”</p>
<p class="indent">When the two men fell, the scream of the crowd pinned the audiometer in
            Control Room 3B into the red.</p>
<p class="indentt">Rear Admiral William Rausch, the director of the CIA, was reading
            “Heard on the Street” in <em class="italic">The Wall Street
                Journal</em> when the red phone in his bedroom rang.</p>
<p class="indent">“Yes, sir?” he said.</p>
<p class="indent">“Are you watching television?”</p>
<p class="indent">“No …”</p>
<p class="indent">“You should be.”</p>
<p class="indent">Then the line went dead.</p>
<p class="indent">Rausch slid out of bed and snapped on the set. He had seen men shot
            before, had ordered men into combat, to their certain death. But what he saw before him
            on the nineteen-inch screen struck him with a terror beyond all knowing.</p>
<p class="indentt">In the NBC mobile unit at the bottom of the Capitol steps, the director
            was screaming into his headset, “Grant! Get up there! Get on the
            stage! Get the stiffs! Fight for your shot!”</p>
<p class="indent">The monitor in the center of the console showed swirling images of
            pandemonium on the rostrum as the NBC cameraman <a id="page_10"></a>fought to climb up
            onto the platform. Two Secret Service men with Uzi submachine guns rushed through the
            crowd. One of them pointed to the Russell Building across the street.</p>
<p class="indent">“On the roof! Move out! Move out!” he
            shouted into his walkie-talkie.</p>
<p class="indent">The NBC cameraman pushed past the Secret Service, pushed into the center
            of the melee on the rostrum. The bodies of Terry Fallon and Octavio Martinez lay side by
            side, slack as broken dolls. In the puddle of blood spreading around them, medics and
            Secret Service knelt and feverishly administered first aid. But reporters and still
            cameramen and news crews had surged up onto the platform, making it almost impossible
            for the medics to work. Secret Service men with drawn sidearms struggled to push back
            the mob of newsmen.</p>
<p class="indent">On the center monitor, a blond woman—Sally Crain—fought
            through the ring of reporters, looked down at the bodies at her feet. Her scream of
            desperation cut the air.</p>
<p class="indentt">For Sally it was as if her whole life, her whole future, her whole being
            climaxed in that momentary roar of gunfire. She had raced for the stairs only to be
            knocked to her knees and almost trampled by the dignitaries fleeing down and away. By
            the time she had gathered herself, reporters were rushing the platform and the swell of
            them carried her up toward the ghastly scene of death. A ring of still photographers and
            video cameramen had gathered around the bodies, weaving and shoving and fighting each
            other for a clear shot of the fallen men.</p>
<p class="indent">Sally had tried to push through them. Then she tried to fight through
            them. Finally, she beat at their backs with her fists until they made room for her. Then
            she had caught sight of Terry.</p>
<p class="indent">His suit was saturated with blood. A medic was cutting away his jacket. In
            the right side of his shirt, just above his beltline, was a pumping wound. Frantically,
            the medic tore the shirt away, struggled to close the wound. A Secret Service man tried
            to push the reporters and Sally back. But she knew where her place was. She went to her
            knees and took Terry’s head in her lap and held him as though by pressing his
            white face against her body she could force her strength into him.</p>
<p class="indentt">The last person on the Capitol steps who realized what had happened was
            Terry Fallon. One moment he was embracing Martinez, the next moment Martinez seemed to
            jump up and <a id="page_11"></a>land on him with his full weight. Terry staggered
            backward a step, trying to help Martinez regain his balance. Then a piercing pain in his
            right side made his leg buckle, and he fell backward into a row of empty folding chairs.
            The back of his head hit the bare boards of the rostrum, and his forehead filled with a
            dull, numbing pain. He felt his stomach chum with nausea. And then his jacket and
            trousers were damp and hot as though he had wet himself. It was only when he looked down
            and saw that his shirt and pants were drenched with blood, that he realized he had been
            shot. His eyes rolled back in his head, and he blacked out.</p>
<p class="indent">Then, a moment or an eternity later, he heard a distant voice calling his
            name. It was Sally’s voice and it was full of pain. He wanted to sleep, wanted to
            stay in the dark region beyond the agony. But it hurt him to hear the pain in
            Sally’s voice, hurt him more than the pain of the wound in his side. He kicked
            back at the darkness and rose to the sound as a deep swimmer rises to the light, and he
            turned to the sound and opened his eyes to see if he could find Sally and console
            her.</p>
<p class="indent">Sally was on her hands and knees beside him, holding his head to her body,
            her skirt and hands covered with blood. There was such stark terror in her face that he
            thought for a moment she had been shot. But when she saw his eyes open, a wave of relief
            swept the terror from her face. Then he knew that he was the one who was wounded, who
            must be brave, who must carry on despite the pain.</p>
<p class="indent">He grasped her hand and tried to rise. But the wound in his side dragged
            him down like a great weight and the throbbing in his forehead blinded and humbled
            him.</p>
<p class="indent">“Senator, stay down!” a man’s gruff voice
            commanded beside him.</p>
<p class="indent">But he wouldn’t stay down. He fought to rise on one elbow.
            “Help me, Sally.”</p>
<p class="indent">And despite the Secret Service clustering around him, despite the medics
            trying to hold him, Terry Fallon rose and looked around. What he saw was chaos.</p>
<p class="indent">Like jackals, the cameramen and reporters had ringed the body of the
            fallen Martinez with glaring tungsten lights and flashing strobes. The colonel lay on
            his back in a puddle of blood and shattered tissue, his head thrown back, his eyes and
            mouth open, while a medic worked frantically at CPR. But the fluid in his eyes was
            already beginning to cloud and dry, and Terry could tell that Octavio Martinez was
            dead.</p>
<a id="page_12"></a>
<p class="indent">Terry pushed away from the hovering Secret Service, shouldered past the
            shouting cameramen, stumbled through the line of still photographers in front of him. He
            steadied himself on Sally’s shoulder and pushed past all of them toward the
            podium and the microphones.</p>
<p class="indent">Steve Chandler was screaming into the intercom that connected him to the
            director in the NBC mobile unit in Washington. “Stay on him! Stay on
            Fallon! Jesus Christ, somebody get me that wound!”</p>
<p class="indent">Suddenly, the torn edge of Terry’s bleeding shirt appeared in
            close-up on the monitor.</p>
<p class="indent">Chandler shouted, “His eyes! Holy Christ! Shoot his
            eyes!”</p>
<p class="indent">And they were there in the monitor, burning resolute through the pain as
            Terry groped his way to the podium.</p>
<p class="indent">“Home base!” Chandler shouted. “The
            podium! Take one! Open his mike!”</p>
<p class="indent">Terry clung to the podium, trying to maintain his balance. Sally held him
            under the elbow. Her hands and face were covered with his blood.</p>
<p class="indent">Terry tried to speak, and his voice cracked with pain. “…
            can you … hear me? … anyone…?”</p>
<p class="indent">In the rush and dither of the crowd clambering onto the rostrum, in the
            madness of shouting men and women, his thin, pained voice went ignored.</p>
<p class="indent">Terry gripped the podium, steadied himself. “How can we …
            turn our backs … while our friends … lie dying?”</p>
<p class="indent">In the mobile unit the director was shouting, “Chandler? Are you
            getting this?” And Chandler’s voice roared back in his headset,
            “You’re live on network from coast-to-coast!”</p>
<p class="indentt">No one who was watching television that day ever forgot what he saw.
            Decades later, you could ask an old man or a middle-aged woman, and he could tell you
            where he had been, what he was doing at the very moment Terry Fallon was shot. It was
            like hearing the news of Jack Kennedy’s death, or Bobby’s or Martin Luther
            King’s. It was an event that froze the moment in time so that the rest of the day
            and the month and the year vanished from memory.</p>
<p class="indent">Into some 75 million homes that day came the image of a tall, young
            American, his white shirt and red tie stained to bloody scarlet. There were streaks of
            blood drying on his face, and it took a young, blond woman and a graying Secret Service
                <a id="page_13"></a>man to support his elbows while he struggled to find words for
            what he and they and every American was feeling at that moment.</p>
<p class="indent">There was a haunting, distant burning in his eyes, like the eyes of a
            voyager who has returned from the unknown with a revelation. Behind him, a priest with
            bloody hands was administering the last rites to a fallen soldier. Behind him, armed men
            and shocked reporters rushed to and fro. But at the podium, above the Great Seal of the
            United States, Terry Fallon stood and pieced together the words that would make his
            country understand.</p>
<p class="indent">“This man … was not a politician. He was … just a man
            … with a simple man’s dream … that his children should live
            free.”</p>
<p class="indent">In his commentary on the <em class="italic">NBC Nightly News</em>,
            John Chancellor recalled his first meeting with Terry Fallon this way: “It was
            hard to believe that anyone could rise to a seat in the Senate of the United States
            without the patronage of special interests, without the taint of political compromise,
            without the smell of cigar smoke and back-room deals clinging to his clothes. But Terry
            Fallon had.”</p>
<p class="indent">Everywhere you turned that day—television, radio,
            newspapers—Terry’s words rang and his face stared back, an accusation and
            a challenge.</p>
<p class="indent">“Octavio Martinez died … to bring us the truth,”
            Terry said, and clung to the podium against the rising dizziness. “His fight is
            our fight. His cause … is our cause. And his dream … must not be buried
            with him.”</p>
<p class="indent">Dan Rather and Diane Sawyer watched the replay together that night on a
                <em class="italic">CBS Special Report:</em> “Death of a Hero.”
            Rather had once visited the Contra staging areas in Honduras, had once interviewed
            Martinez among his army of teenagers and <em class="italic">campesinos</em>.
            “But is it our fight, Dan?” Sawyer demanded. “Is it ours—or
            is it the politicians’?” Rather held the bridge of his nose for a moment.
            Then he said, “Diane, either it’s our fight—or it’s our
            children’s …”</p>
<p class="indent">It was after midnight when Ted Koppel wrapped up his interviews with
            Secretary of State Cranston and Tomás Borge, the interior minister of the
            Republic of Nicaragua. The two men had exchanged half an hour of bitter recriminations
            and angry accusations. Koppel had wanted to close <em class="italic">Nightline</em>
            by asking the central question that had emerged from the melee—so he reran the
            last words Terry Fallon had spoken before the medics forced him to lie down on the
            stretcher that took him to Walter Reed Hospital.</p>
<a id="page_14"></a>
<p class="indent">It was a familiar image by now. The milling crowd of Secret Service men
            and reporters had fallen still and silent in a ring around the three people standing at
            the podium. The gray-haired Secret Service man to Terry’s left was weeping
            openly. Sally Crain’s clear blue eyes and brave face looked up at Terry with
            trust and adoration. With bloodied hands they held his elbows, sustaining him. Over his
            shoulder, the flag before the Capitol snapped in the rising breeze.</p>
<p class="indent">“How long, America?” Terry said, and his pain-ridden voice
            rang with courage. “How long will men like this suffer and die before
            we’re willing to listen? How long before we lift our heads … and hearts
            … and fists … so that their children—and our own—can live in
            freedom in a world at peace?”</p>
<p class="indent">When he finished, there was only silence. But it was the silence of the
            ages.</p>
<p class="indentt">A man named Lou Bender watched it all.</p>
<p class="indent">To him, Martinez’s assassination was an awkward
            inconvenience—an inconvenience that would have to be dealt with quickly,
            decisively. But it was an inconvenience that could be managed.</p>
<p class="indent">To him, Terry’s words were simply the jangled rambling of an
            idealist drifting into shock.</p>
<p class="indent">To him, the deluge of network coverage—the <em class="italic">Special Segments, Special Reports, Nightline, Today, Good Morning America</em>,
            and the <em class="italic">CBS Morning News</em>—were a textbook example of
            how the three networks gorged themselves on violence, until they bloated the insane act
            of one illiterate terrorist into an event of international consequence. When Lou Bender
            turned off his television set at seven-thirty the next morning, he was disgusted with the whole
            pathetic circus.</p>
<p class="indent">But he was the second person in the world to understand that if Terry
            Fallon survived his wounds, he was destined to become the most powerful man on
            earth.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 07_Chapter01.vue?4717a0ca